import { plural } from '@/lib/plural';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi, type AdminRollUpFamily, type AdminRollUpLinked } from '@/lib/apiClient';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { isNotFoundError } from '@/lib/httpErrors';

/**
 * Admin → Job Roll-ups (#613): which jobs roll up to which.
 *
 * A parent job's Financials tab can show itself combined with the jobs that roll up to it. This is
 * where that family is set up, so the next one does not need a migration.
 *
 * ⚠️ A FAILED LOAD IS NOT AN EMPTY LIST. "No roll-ups yet" on an outage would invite someone to set
 * a family up again over the top of one that exists. A failure says so and offers a retry.
 *
 * Jobs are typed by JOB NUMBER, the way everyone here names a job. The server refuses a number that
 * matches no job (the common case: the job has not been added to the Project Tracker yet) and says
 * so by name, and it links all or none, so a typo never leaves half a family linked.
 */

const css = `
  .rlu-intro { font-size: var(--ew-fs-body-sm); color: var(--ew-n-500); margin: 0 0 20px; max-width: 72ch; line-height: 1.5; }
  .rlu-empty { padding: 32px; text-align: center; color: var(--ew-n-500); font-size: var(--ew-fs-body-sm); }
  .rlu-empty p { margin: 0 0 12px; }
  .rlu-card { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); background: #fff; padding: 16px; margin-bottom: 20px; }
  .rlu-card h3 { margin: 0 0 12px; font: var(--ew-fw-semibold) var(--ew-fs-body)/1.3 var(--ew-font); color: var(--ew-dark); }
  .rlu-form { display: grid; grid-template-columns: minmax(0, 200px) minmax(0, 1fr) auto; gap: var(--ew-s-3); align-items: end; }
  @media (max-width: 760px) { .rlu-form { grid-template-columns: 1fr; } }
  .rlu-field label { display: block; font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: .06em; text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 6px; }
  .rlu-field input { width: 100%; box-sizing: border-box; padding: 8px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md); font: inherit; font-size: var(--ew-fs-body-sm); }
  .rlu-hint { grid-column: 1 / -1; font-size: var(--ew-fs-meta); color: var(--ew-n-500); margin: 0; }
  .rlu-btn { padding: 8px var(--ew-s-4); border: 1px solid var(--ew-n-200); background: #fff; border-radius: var(--ew-r-md); font: inherit; font-size: var(--ew-fs-body-sm); cursor: pointer; min-height: 36px; }
  .rlu-btn:hover:not(:disabled) { background: var(--ew-n-50); }
  .rlu-btn:disabled { opacity: .55; cursor: default; }
  .rlu-btn.primary { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .rlu-btn.primary:hover:not(:disabled) { background: var(--ew-dark); opacity: .9; }
  .rlu-btn.small { padding: 4px 10px; min-height: 28px; }
  .rlu-btn.danger { color: #9A2B1F; border-color: #E8C4BE; }
  .rlu-msg { margin: 12px 0 0; padding: 10px 12px; border-radius: var(--ew-r-md); font-size: var(--ew-fs-body-sm); line-height: 1.5; white-space: pre-line; }
  .rlu-msg.ok { background: #E8F3EA; color: #1F5A2B; border: 1px solid #C5DEC9; }
  .rlu-msg.err { background: #FBEEE1; color: #8A4E12; border: 1px solid #F0D9C0; }
  .rlu-count { font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.3 var(--ew-font); color: var(--ew-dark); margin: 0 0 8px; }
  .rlu-fam { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); background: #fff; margin-bottom: 12px; }
  .rlu-parent { display: flex; align-items: baseline; gap: var(--ew-s-3); flex-wrap: wrap; padding: 12px 16px; border-bottom: 1px solid var(--ew-n-100); background: var(--ew-n-50); }
  .rlu-job { font-variant-numeric: tabular-nums; font-size: var(--ew-fs-body-sm); font-weight: var(--ew-fw-semibold); }
  .rlu-job a { color: var(--ew-green); }
  .rlu-title { font-size: var(--ew-fs-meta); color: var(--ew-n-500); }
  .rlu-tag { font-size: var(--ew-fs-meta); border: 1px solid currentColor; border-radius: var(--ew-r-pill); padding: 0 6px; color: var(--ew-n-500); }
  .rlu-tag.warn { color: #B5611A; }
  .rlu-child { display: flex; align-items: center; gap: var(--ew-s-3); flex-wrap: wrap; padding: 10px 16px; border-bottom: 1px solid var(--ew-n-100); }
  .rlu-child:last-child { border-bottom: 0; }
  .rlu-child .rlu-spacer { flex: 1 1 auto; }
  .rlu-when { font-size: var(--ew-fs-meta); color: var(--ew-n-500); }
  .rlu-confirm { display: inline-flex; align-items: center; gap: 6px; font-size: var(--ew-fs-meta); }
  .rlu-hist { list-style: none; margin: 0; padding: 0; font-size: var(--ew-fs-body-sm); }
  .rlu-hist li { padding: 6px 0; border-bottom: 1px solid var(--ew-n-100); }
  .rlu-hist li:last-child { border-bottom: 0; }
  .rlu-hist .rlu-when { margin-right: 8px; }
`;

/** Job numbers typed as a list: commas, spaces or new lines between them, repeats ignored. */
export function parseJobNumbers(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];
}

/** The server's refusal for a job that rolls up to nothing (`NOT_LINKED` in roll-ups.service.ts): change both together. */
const NOT_LINKED = /does not roll up to anything/;

/** Mirrors `MAX_CHILDREN_PER_CALL` on the server, which refuses more; this only saves the round trip. */
const MAX_AT_ONCE = 50;


function linkedSummary(r: AdminRollUpLinked): string {
  const bits: string[] = [];
  if (r.linked.length) bits.push(`${plural(r.linked.length, 'job')} now ${r.linked.length === 1 ? 'rolls' : 'roll'} up to job ${r.parent.jobNumber}: ${r.linked.map((j) => j.jobNumber).join(', ')}.`);
  if (r.alreadyLinked.length) bits.push(`Already linked, unchanged: ${r.alreadyLinked.map((j) => j.jobNumber).join(', ')}.`);
  return bits.join(' ');
}

export default function RollUpsTab() {
  const qc = useQueryClient();
  const [parentText, setParentText] = useState('');
  const [childText, setChildText] = useState('');
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // A success message describes the list that was just sent, so it goes once a new one is typed. An ERROR
  // stays: the admin is editing the list to fix exactly what it names.
  const dropSuccessNotice = () => setNotice((n) => (n?.kind === 'ok' ? null : n));
  /** The child whose Remove is waiting on a second click. */
  const [confirming, setConfirming] = useState<number | null>(null);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'roll-ups'],
    queryFn: adminApi.listRollUps,
  });

  // A change here changes what a parent's Financials tab offers, so its family and roll-up reads are
  // stale too (['financials', jobId, 'family'] and ['financials', jobId, 'rollup', month?]). Nothing
  // else under 'financials' depends on the family, so nothing else is touched.
  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'roll-ups'] }),
      qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'financials' && (q.queryKey[2] === 'family' || q.queryKey[2] === 'rollup'),
      }),
    ]);
  };

  const link = useMutation({
    mutationFn: (v: { parent: string; children: string[] }) => adminApi.linkRollUp(v.parent, v.children),
    onSuccess: async (r) => {
      setNotice({ kind: 'ok', text: linkedSummary(r) });
      setChildText('');
      await refresh();
    },
    // The server's message names every refused job and why, one per line; nothing was linked.
    onError: (e) => setNotice({ kind: 'err', text: e instanceof Error ? e.message : 'Could not link those jobs. Nothing was changed.' }),
  });

  const unlink = useMutation({
    mutationFn: (childProjectId: number) => adminApi.unlinkRollUp(childProjectId),
    onSuccess: async (r) => {
      setNotice({ kind: 'ok', text: `Job ${r.jobNumber} no longer rolls up to job ${r.parentJobNumber}.` });
      await refresh();
    },
    onError: async (e) => {
      // Someone else removed it first: the list on screen was stale, and now it is not. Only the
      // server's own "not linked" refusal means that: the web app and the API deploy separately, and a
      // 404 from an API that predates this route (or any other 404) must not be reported as success.
      if (isNotFoundError(e) && e instanceof Error && NOT_LINKED.test(e.message)) {
        setNotice({ kind: 'ok', text: 'That job was already removed.' });
        await refresh();
        return;
      }
      setNotice({ kind: 'err', text: e instanceof Error ? e.message : 'Could not remove it. Nothing was changed.' });
    },
    onSettled: () => setConfirming(null),
  });

  const busy = link.isPending || unlink.isPending;

  if (isLoading) return <div className="rlu-empty">Loading…</div>;

  if (isError || !data) {
    return (
      <>
        <style>{css}</style>
        <div className="rlu-empty">
          <p>Could not load the roll-ups. Nothing has been changed.</p>
          <button type="button" className="rlu-btn" onClick={() => { void refetch(); }} disabled={isFetching}>
            {isFetching ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      </>
    );
  }

  if (!data.available) {
    return (
      <>
        <style>{css}</style>
        <div className="rlu-empty">
          <p>Roll-ups are not set up on this database yet.</p>
          <p>The roll-up table has to be created first (the migration <code>2026-10-15_project-roll-up.sql</code>).</p>
        </div>
      </>
    );
  }

  const children = parseJobNumbers(childText);
  const parent = parentText.trim();
  const tooMany = children.length > MAX_AT_ONCE;
  const canSubmit = !busy && parent !== '' && children.length > 0 && !tooMany;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setNotice(null);
    link.mutate({ parent, children });
  };

  const family = (f: AdminRollUpFamily) => (
    <div className="rlu-fam" key={f.parent.projectId}>
      <div className="rlu-parent">
        <span className="rlu-job"><Link to={`/projects/${f.parent.projectId}`}>{f.parent.jobNumber}</Link></span>
        <span className="rlu-title">{f.parent.title}</span>
        <span className="rlu-tag">Parent</span>
        {f.parent.deleted && <span className="rlu-tag warn">Job deleted</span>}
        {f.parentIsAlsoChild && (
          <span className="rlu-tag warn">
            This job also rolls up to another job. A chain cannot be combined; remove one of the links.
          </span>
        )}
      </div>
      {f.children.map((c) => (
        <div className="rlu-child" key={c.projectId}>
          <span className="rlu-job"><Link to={`/projects/${c.projectId}`}>{c.jobNumber}</Link></span>
          <span className="rlu-title">{c.title}</span>
          {c.deleted && <span className="rlu-tag warn">Job deleted</span>}
          <span className="rlu-spacer" />
          <span className="rlu-when">Linked {fmtDate(c.linkedAt)}</span>
          {confirming === c.projectId ? (
            <span className="rlu-confirm">
              Remove job {c.jobNumber} from this roll-up?
              <button type="button" className="rlu-btn small danger" disabled={busy} onClick={() => unlink.mutate(c.projectId)}>
                Yes, remove
              </button>
              <button type="button" className="rlu-btn small" disabled={busy} onClick={() => setConfirming(null)}>
                Keep
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="rlu-btn small"
              disabled={busy}
              aria-label={`Remove job ${c.jobNumber} from the roll-up under ${f.parent.jobNumber}`}
              onClick={() => { setNotice(null); setConfirming(c.projectId); }}
            >
              Remove
            </button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      <style>{css}</style>
      <p className="rlu-intro">
        A parent job&rsquo;s Financials tab can show itself combined with the jobs that roll up to it, as one set of
        figures. Set up the family here by job number. A job rolls up to one parent, and a job cannot be both a parent
        and a child. A job has to be in the Project Tracker before it can be linked.
      </p>

      <form className="rlu-card" onSubmit={submit} aria-label="Add jobs to a roll-up">
        <h3>Add jobs to a roll-up</h3>
        <div className="rlu-form">
          <div className="rlu-field">
            <label htmlFor="rlu-parent">Parent job</label>
            <input id="rlu-parent" value={parentText} onChange={(e) => { setParentText(e.target.value); dropSuccessNotice(); }} placeholder="10910" autoComplete="off" maxLength={50} />
          </div>
          <div className="rlu-field">
            <label htmlFor="rlu-children">Jobs that roll up to it</label>
            <input id="rlu-children" value={childText} onChange={(e) => { setChildText(e.target.value); dropSuccessNotice(); }} placeholder="10961, 10841, 10944" autoComplete="off" />
          </div>
          <button type="submit" className="rlu-btn primary" disabled={!canSubmit}>
            {link.isPending ? 'Linking…' : 'Link jobs'}
          </button>
          <p className="rlu-hint">
            {tooMany
              ? `At most ${MAX_AT_ONCE} jobs at a time (${children.length} entered).`
              : 'Separate job numbers with commas or spaces. If any one is refused, none are linked.'}
          </p>
        </div>
        {notice && <div className={`rlu-msg ${notice.kind}`} role={notice.kind === 'err' ? 'alert' : 'status'}>{notice.text}</div>}
      </form>

      {data.families.length === 0 ? (
        <div className="rlu-empty"><p>No jobs roll up to another job yet.</p></div>
      ) : (
        <>
          <h3 className="rlu-count">{plural(data.families.length, 'roll-up')}</h3>
          {data.families.map(family)}
        </>
      )}

      {data.history.length > 0 && (
        <div className="rlu-card">
          <h3>Recent changes</h3>
          <ul className="rlu-hist">
            {data.history.map((h, i) => (
              <li key={`${h.at}-${i}`}>
                <span className="rlu-when">{fmtDateTime(h.at)}{h.by ? ` · ${h.by}` : ''}</span>
                {h.detail ?? h.action}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
